import { useCallback, useLayoutEffect, useState, useRef } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { TextFieldProps, TextFieldVariants } from '@mui/material';
import {
  useEditorStore,
  useFlowBuilder2Context,
  useFlowBuilder2StepValidators,
  FlowBuilder2StepFieldNamesEnum,
} from '@diana/core';
import { TextFieldWithValidationTooltip } from '../TextFieldWithValidationTooltip/TextFieldWithValidationTooltip';
import { useEntityFieldState } from '../../Support/useEntityFieldState';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';

export function StepNameField<V extends TextFieldVariants>(
  props: TextFieldProps<V> & {
    stepId: string;
    autoWidth?: boolean;
    disableValidationResultTooltip?: boolean;
  },
) {
  const {
    stepId,
    onChange,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    autoWidth,
    disableValidationResultTooltip,
    ...textFieldProps
  } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { updateAndSaveStepById } = useFlowBuilder2Context();

  const getFlow = useEditorStore((s) => s.getFlow);
  const displayName = useEditorStore((s) => {
    return s.flow?.steps.find((step) => step.stepId === stepId)?.stepDisplayName ?? '';
  }, isEqual);

  const [value, setValue] = useState<string>(displayName);
  const valueRef = useRef(value);

  const validators = useFlowBuilder2StepValidators();
  const fieldState = useEntityFieldState({
    objectId: stepId,
    objectType: 'step',
    fieldName: FlowBuilder2StepFieldNamesEnum.DisplayName,
  });

  const validate = validators[FlowBuilder2StepFieldNamesEnum.DisplayName];

  const validateDisplayName = useCallback(
    (stepDisplayName: string) => {
      const flow = getFlow();
      const step = flow?.steps.find((step) => step.stepId === stepId);
      const result = flow && step ? validate({ flow, step: { ...step, stepDisplayName } }) : null;
      if (!isEqual(fieldState.validationResult, result)) fieldState.setValidationResult(result);
    },
    [getFlow, validate, fieldState, stepId],
  );

  const saveDisplayName = useDebouncedCallback((stepDisplayName: string) => {
    updateAndSaveStepById(stepId, (draft) => ({ ...draft, stepDisplayName }));
  }, 500);

  useLayoutEffect(() => {
    if (value !== displayName) setValue(displayName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayName]);

  useLayoutEffect(() => {
    valueRef.current = value;
  }, [value]);

  useLayoutEffect(() => {
    return () => saveDisplayName.flush();
  }, [saveDisplayName]);

  return (
    <TextFieldWithValidationTooltip
      placeholder={t('labels.untitledStep')}
      error={fieldState.validationResult?.type === 'error'}
      validationResult={disableValidationResultTooltip ? null : fieldState.validationResult}
      {...textFieldProps}
      autoWidth={autoWidth}
      value={value}
      onChange={(e) => {
        fieldState.emitDirty();
        setValue(e.target.value);
        saveDisplayName(e.target.value);
        validateDisplayName(e.target.value);
        if (onChange) onChange(e);
      }}
      onBlur={(e) => {
        fieldState.emitTouched();
        validateDisplayName(e.target.value);
        if (onBlur) onBlur(e);
      }}
    />
  );
}
